import { FilterDefinition, Rule } from "@monolith-forensics/monolith-ui";
import { GetStorageQuery } from "./types";
import type {
  LegacyOperatorKey,
  LegacyStorageCondition,
  LegacyStorageQuery,
} from "../storage.api.types";

/**
 * Translates the modern mfui Table state (a `filter` RuleGroup + `sort_by`/
 * `sort_dir`) into the LEGACY envelope the storage list endpoint understands.
 *
 * `POST /storage/query` (legacy `getStorage`) does NOT accept the mfui RuleGroup
 * `filter` that `/evidence/query` does — it only reads a nested
 * `{ query: { conditions, order, search, pageSize, page, case_id } }` wrapper and
 * runs `conditions` through `queryConditionResolver`. This adapter is the
 * frontend bridge (ENG-1479, Option A) until the storage backend gains a
 * RuleGroup-aware path (ENG-1363). See the resolver contract in
 * monolith-api `src/services/utilities/Query-Condition-Resolver.js`.
 */

// This will be refactored/ eliminated in future PRs as it will no longer be needed, but for now is here to keep PR in scope

/**
 * Maps an mfui operator `.value` to the `queryConditionResolver` operator key.
 * Inclusive (`>=`/`<=`), `notBetween`, and `isEmpty`/`isNotEmpty` are
 * intentionally absent — the storage filter definitions never expose them and
 * the resolver does not support them (ENG-1315/1317 backend limitation).
 */
const OPERATOR_MAP: Record<string, LegacyOperatorKey> = {
  // "equals", not "is": the resolver treats them identically, but the storage
  // service's fieldMapFn only rewrites location_path (a SELECT alias, illegal
  // in WHERE) to its real SQL expression for "equals"/"doesNotEqual".
  "=": "equals",
  "!=": "doesNotEqual",
  ">": "isAfter",
  "<": "isBefore",
  between: "isBetween",
  in: "isAnyOf",
  nin: "isNoneOf",
  contains: "contains",
  doesNotContain: "doesNotContain",
};

const isCustomField = (field: string) => field.startsWith("custom_field_");

/** Resolver-relevant `operator.type` per filter dimension (only "date" affects SQL). */
const buildTypeMap = (
  filterDefinitions: FilterDefinition[]
): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const def of filterDefinitions) {
    map[def.dataField] =
      def.inputType === "datetime" || def.inputType === "date"
        ? "date"
        : def.inputType === "number"
        ? "number"
        : "text";
  }
  return map;
};

/**
 * Packs a rule's value to match how `queryConditionResolver` consumes args for
 * each operator. Custom fields always keep the array (their resolver branches
 * spread/iterate it); standard fields need a scalar for `is`/`contains`/
 * `doesNotContain` and the array for everything else.
 */
const packValue = (
  field: string,
  operator: LegacyOperatorKey,
  value: string[]
): unknown => {
  if (isCustomField(field)) return value;

  switch (operator) {
    case "equals":
    case "contains":
    case "doesNotContain":
      return value[0];
    default:
      return value;
  }
};

const toCondition = (
  rule: Rule,
  typeMap: Record<string, string>
): LegacyStorageCondition | null => {
  const operator = OPERATOR_MAP[rule.operator?.value];
  if (!operator) return null;

  const field = rule.dataField;
  const value = rule.value ?? [];

  return {
    field,
    operator: { operator, type: typeMap[field] || "text" },
    value: packValue(field, operator, value),
  };
};

export const resolveStorageQuery = (options: {
  resolvedQuery: GetStorageQuery;
  page: number;
  filterDefinitions: FilterDefinition[];
}): LegacyStorageQuery => {
  const { resolvedQuery, page, filterDefinitions } = options;
  const typeMap = buildTypeMap(filterDefinitions);

  const conditions = (resolvedQuery.filter?.rules ?? [])
    .map((rule) => toCondition(rule, typeMap))
    .filter((c): c is LegacyStorageCondition => c !== null);

  const order = resolvedQuery.sort_by
    ? {
        field: resolvedQuery.sort_by,
        sort: resolvedQuery.sort_dir ?? "asc",
      }
    : undefined;

  return {
    query: {
      ...(conditions.length > 0 ? { conditions } : {}),
      ...(order ? { order } : {}),
      search: resolvedQuery.search ?? null,
      pageSize: resolvedQuery.pageSize,
      page,
      case_id: resolvedQuery.case_id ?? null,
    },
  };
};
